import type React from "react";
import { useTranslation } from "react-i18next";
import type { PortfolioTemplate } from "../services/portfolio-api.service";
import { PORTFOLIO_TEMPLATES } from "../template-definitions";

export interface TemplateSwitcherProps {
	selectedTemplate: PortfolioTemplate;
	savedTemplate: PortfolioTemplate;
	onSelectTemplate: (template: PortfolioTemplate) => void;
	isPublished?: boolean;
	dataTestId?: string;
}

export function TemplateSwitcher({
	selectedTemplate,
	savedTemplate,
	onSelectTemplate,
	isPublished = false,
	dataTestId = "portfolio-template-switcher",
}: TemplateSwitcherProps): React.ReactElement {
	const { t } = useTranslation();

	return (
		<div
			className="flex flex-wrap items-center gap-2 rounded-2xl border border-dashboard-border bg-dashboard-surface p-2.5"
			data-testid={dataTestId}
		>
			{PORTFOLIO_TEMPLATES.map((tmpl) => {
				const isSelected = selectedTemplate === tmpl.id;
				const isSaved = savedTemplate === tmpl.id;
				return (
					<button
						key={tmpl.id}
						type="button"
						aria-pressed={isSelected}
						onClick={() => onSelectTemplate(tmpl.id)}
						className={`group relative flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-medium transition cursor-pointer ${
							isSelected
								? "bg-dashboard-accent text-dashboard-on-fill shadow-sm font-semibold"
								: "bg-dashboard-paper text-dashboard-text hover:bg-dashboard-warm-100 hover:text-dashboard-accent-dark border border-dashboard-border/60"
						}`}
					>
						<span data-testid={`${tmpl.id}-mini`} className="hidden" />
						<p className="text-xs font-semibold m-0">{tmpl.name}</p>
						{isSaved && (
							<span
								className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
									isSelected
										? "bg-white/20 text-white"
										: isPublished
											? "bg-emerald-100 text-emerald-800 border border-emerald-300"
											: "bg-dashboard-warm-200 text-dashboard-muted-dark border border-dashboard-border"
								}`}
							>
								<span
									className={`h-1.5 w-1.5 rounded-full ${
										isSelected
											? "bg-white"
											: isPublished
												? "bg-emerald-500"
												: "bg-dashboard-muted"
									}`}
								/>
								{isPublished
									? t("portfolio.liveOnPublic", "Live on Public")
									: t("portfolio.savedTemplate", "Saved Template")}
							</span>
						)}
					</button>
				);
			})}
		</div>
	);
}
